import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContextZustand';
import { Link, useLocation } from 'react-router-dom';
import { useToast } from '../UI';
import {
  getMenuItemsForPermissions,
  isEffectivePermissionsEmpty,
  canAccessStock,
  ROUTES,
} from '../../constants/permissions';
import { useEstablishmentStore } from '../../store/establishmentStore';
import { useSettingsStore } from '../../store/settingsStore';
import { APP_CONFIG } from '../../constants/appConfig';
import {
  ChartBarIcon,
  CalendarIcon,
  UsersIcon,
  BriefcaseIcon,
  CubeIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentListIcon,
  BuildingLibraryIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  BellAlertIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { getMediaUrl } from '../../utils/mediaUrl';

const Icon: React.FC<{ name: string; className?: string; style?: React.CSSProperties }> = ({
  name,
  className = '',
  style,
}) => {
  const iconComponents: Record<
    string,
    React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  > = {
    dashboard: ChartBarIcon,
    appointment: CalendarIcon,
    users: UsersIcon,
    customers: UsersIcon,
    professionals: BriefcaseIcon,
    works: ClipboardDocumentListIcon,
    stock: CubeIcon,
    billing: BanknotesIcon,
    billingAccounts: BuildingLibraryIcon,
    subscription: CreditCardIcon,
    reports: DocumentChartBarIcon,
    settings: CogIcon,
    logout: ArrowRightOnRectangleIcon,
    notifications: BellAlertIcon,
    onboarding: ClipboardDocumentCheckIcon,
  };

  const IconComponent = iconComponents[name] || ClipboardDocumentListIcon;

  return <IconComponent className={className} style={style} />;
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const { addToast } = useToast();
  const { establishment } = useEstablishmentStore();
  const { branding } = useSettingsStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  const isRestrictedBySubscription =
    user?.needsSubscription === true ||
    user?.accessLevel === 'RESTRICTED' ||
    user?.accessLevel === 'BLOCKED';
  const subscriptionAllowedPaths: string[] = [
    ROUTES.SUBSCRIPTION,
    ROUTES.SETTINGS,
    ROUTES.NOTIFICATIONS,
  ];
  const rawMenuSections = getMenuItemsForPermissions(user?.permissions, false);
  const canAccessStockSection = canAccessStock(user ?? null, establishment ?? null);
  type MenuItem = { name: string; href: string; icon: string };
  const menuSections = rawMenuSections
    .map((section) => {
      const items = section.items as MenuItem[];
      return {
        ...section,
        items: items.filter(
          (item: MenuItem) => item.href !== ROUTES.STOCK || canAccessStockSection
        ),
      };
    })
    .filter((section) => section.items.length > 0) as { title: string; items: MenuItem[] }[];

  // Usuário sem permissões IAM efetivas (onboarding IAM incompleto)
  const isPendingDev = isEffectivePermissionsEmpty(user?.permissions);

  // Pega o nome fantasia do establishment, slogan e logo (configurações salvas)
  const fantasyName = establishment?.fantasyName || APP_CONFIG.name;
  const slogan = branding?.slogan;
  const logoPath = establishment?.logo ?? branding?.logo;
  const logoSrc =
    !logoPath || typeof logoPath !== 'string'
      ? ''
      : logoPath.startsWith('data:') || logoPath.startsWith('http')
        ? logoPath
        : getMediaUrl(logoPath);

  // Verifica se há scroll e se está no final
  useEffect(() => {
    const checkScroll = () => {
      if (navRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = navRef.current;
        const hasScrollContent = scrollHeight > clientHeight;
        const isBottom = scrollHeight - scrollTop - clientHeight < 10;

        setHasScroll(hasScrollContent);
        setIsAtBottom(isBottom);
      }
    };

    checkScroll();
    const navElement = navRef.current;
    if (navElement) {
      navElement.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }

    return () => {
      if (navElement) {
        navElement.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [menuSections]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Erro desconhecido';

      addToast({
        type: 'error',
        title: 'Erro ao fazer logout',
        message: `Ocorreu um problema ao desconectar: ${errorMessage}`,
        duration: 5000,
      });
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}

      <div
        className={`
          fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-lg transform transition-all duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:inset-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        <div className="flex flex-col h-full relative">
          <div className="px-4 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-700"
                style={
                  logoSrc
                    ? undefined
                    : {
                        backgroundColor: 'var(--color-primary)',
                        color: 'var(--color-on-primary)',
                      }
                }
              >
                {logoSrc ? (
                  <img src={logoSrc} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="font-bold text-lg">HIVI</span>
                )}
              </div>
              {!isCollapsed && (
                <div className="ml-3 flex-1 min-w-0">
                  <h1 className="text-xl font-bold admin-text-title dark:text-gray-100 truncate">
                    {fantasyName}
                  </h1>
                  {slogan ? (
                    <p className="text-sm admin-text-muted dark:text-gray-400 truncate italic">
                      "{slogan}"
                    </p>
                  ) : (
                    <p className="text-sm admin-text-muted dark:text-gray-400">Admin Dashboard</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Botão para colapsar/expandir - simples e minimalista */}
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 w-6 h-16 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-r-lg items-center justify-center admin-row-hover dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 shadow-md group"
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="w-4 h-4 admin-text-body dark:text-gray-400 group-hover:admin-text-label dark:group-hover:text-gray-200" />
            ) : (
              <ChevronLeftIcon className="w-4 h-4 admin-text-body dark:text-gray-400 group-hover:admin-text-label dark:group-hover:text-gray-200" />
            )}
          </button>

          <div className="relative flex-1 min-h-0">
            <nav ref={navRef} className="h-full px-4 py-6 space-y-6 overflow-y-auto">
              {/* Aviso quando ainda não há permissões efetivas atribuídas */}
              {isPendingDev && !isCollapsed && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                        Perfil com permissões limitadas
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        As funcionalidades para seu perfil estão em desenvolvimento.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {menuSections.map((section, idx) => (
                <div key={section.title}>
                  {!isCollapsed && (
                    <div className="text-xs font-semibold text-gray-400 dark:admin-text-muted uppercase px-2 mb-2 tracking-wider">
                      {section.title}
                    </div>
                  )}
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = location.pathname === item.href;
                      const isSettingsAndActive = item.href === '/settings' && isActive;
                      const isLockedBySubscription =
                        isRestrictedBySubscription && !subscriptionAllowedPaths.includes(item.href);
                      const linkTarget = isLockedBySubscription
                        ? ROUTES.SUBSCRIPTION
                        : isSettingsAndActive
                          ? `${item.href}?reset=true`
                          : item.href;

                      return (
                        <Link
                          key={item.name}
                          to={linkTarget}
                          onClick={() => {
                            if (isLockedBySubscription) {
                              addToast({
                                type: 'info',
                                title: 'Área bloqueada',
                                message:
                                  'Regularize sua assinatura para liberar esta funcionalidade.',
                              });
                            }
                            onClose();
                          }}
                          title={
                            isLockedBySubscription
                              ? `${item.name} bloqueado até regularizar assinatura`
                              : item.name
                          }
                          className={`
                          group flex items-center text-sm font-medium rounded-2xl transition-colors
                          ${isCollapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'}
                          ${
                            isActive
                              ? 'shadow-md'
                              : isLockedBySubscription
                                ? 'admin-text-muted'
                                : 'admin-text-body dark:text-gray-300'
                          }
                          ${
                            !isActive && !isLockedBySubscription
                              ? 'hover:bg-gray-100 dark:hover:bg-gray-700/60'
                              : ''
                          }
                        `}
                          style={
                            isActive
                              ? {
                                  backgroundColor: 'var(--color-primary)',
                                  color: 'var(--color-on-primary)',
                                }
                              : undefined
                          }
                        >
                          <Icon
                            name={item.icon}
                            className={`w-5 h-5 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}
                            style={
                              isActive
                                ? { color: 'var(--color-on-primary)' }
                                : isLockedBySubscription
                                  ? undefined
                                  : { color: 'var(--color-nav-accent)' }
                            }
                          />
                          {!isCollapsed && <span>{item.name}</span>}
                          {!isCollapsed && isLockedBySubscription && (
                            <span className="ml-auto text-[10px] uppercase tracking-wide admin-text-muted">
                              Bloq.
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                  {idx < menuSections.length - 1 && !isCollapsed && (
                    <div className="my-4 border-t border-gray-100 dark:border-gray-700" />
                  )}
                </div>
              ))}
            </nav>

            {/* Indicador de mais conteúdo abaixo */}
            {hasScroll && !isAtBottom && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent dark:from-gray-800 dark:via-gray-800/90 pointer-events-none flex items-end justify-center pb-2">
                <div className="animate-bounce">
                  <ChevronDownIcon className="w-5 h-5 text-gray-400 dark:admin-text-muted" />
                </div>
              </div>
            )}
          </div>

          <div
            className={`border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 z-10 ${isCollapsed ? 'px-2 py-4' : 'px-4 py-6'}`}
          >
            {/* Botão de Central de Ajuda */}
            <Link
              to="/support"
              onClick={onClose}
              title="Central de Ajuda"
              className={`flex items-center w-full text-sm font-medium admin-text-label dark:text-gray-300 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors mb-3
                ${isCollapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'}
              `}
            >
              <QuestionMarkCircleIcon
                className={`w-5 h-5 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}
                style={{ color: 'var(--color-nav-accent)' }}
              />
              {!isCollapsed && 'Central de Ajuda'}
            </Link>

            {!isCollapsed && (
              <div className="flex items-center mb-4">
                {getMediaUrl(user?.photoUrl || user?.avatar) ? (
                  <img
                    src={getMediaUrl(user?.photoUrl || user?.avatar)}
                    alt={user?.name ?? ''}
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-2xl object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-2xl flex items-center justify-center">
                    <span className="admin-text-body dark:text-gray-300 font-bold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="ml-3">
                  <p className="text-sm font-medium admin-text-title dark:text-gray-100">
                    {user?.name} {user?.lastName}
                  </p>
                </div>
              </div>
            )}

            {isCollapsed && user && (
              <div className="flex justify-center mb-3">
                {getMediaUrl(user?.photoUrl || user?.avatar) ? (
                  <img
                    src={getMediaUrl(user?.photoUrl || user?.avatar)}
                    alt={user?.name ?? ''}
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-2xl object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-2xl flex items-center justify-center">
                    <span className="admin-text-body dark:text-gray-300 font-bold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleLogout}
              title="Sair"
              className={`flex items-center w-full text-sm font-medium text-red-600 dark:text-red-400 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors
                ${isCollapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'}
              `}
            >
              <Icon name="logout" className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'}`} />
              {!isCollapsed && 'Sair'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
